import { httpx } from '../httpx.js';
import { config } from '../config.js';

class BeeTrackDashboard {
  constructor() {
    this.devices = [];
    this.selectedDevice = null;
    this.map = null;
    this.markers = [];
    this.charts = {};
    this.mapboxToken = config.mapboxToken;
    this.dateRange = {
      startDate: null,
      endDate: null,
    };

    // Only initialize if we're on the beeTrack dashboard page
    if (this.isDashboardPage()) {
      this.init();
    }
  }

  isDashboardPage() {
    // Check if the required dashboard elements exist
    return (
      document.querySelector('.dashboard') !== null &&
      document.getElementById('deviceSelect') !== null
    );
  }

  async init() {
    try {
      await this.loadDevices();
      this.setupMap();
      this.setupEventListeners();
      this.updateStats();
    } catch (error) {
      console.error('Failed to initialize dashboard:', error);
      // Only show error if we're actually on the dashboard page
      if (this.isDashboardPage()) {
        this.showError('Failed to load dashboard data');
      }
    }
  }

  async loadDevices() {
    try {
      const response = await httpx.get('/api/v1/beeTrack/dashboard/devices');
      this.devices = response.data.data.devices || [];
      this.populateDeviceSelector();
    } catch (error) {
      console.error('Failed to load devices:', error);
      throw error;
    }
  }

  populateDeviceSelector() {
    const deviceSelect = document.getElementById('deviceSelect');
    deviceSelect.innerHTML = '';

    this.devices.forEach(device => {
      const option = document.createElement('option');
      option.value = device.id;
      option.textContent = device.deviceId;
      deviceSelect.appendChild(option);
    });

    // Automatically select the first device
    if (this.devices.length > 0) {
      deviceSelect.value = this.devices[0].id;
      this.selectDevice(this.devices[0].id);
    }
  }

  setupMap() {
    if (!mapboxgl) {
      console.error('Mapbox GL JS not loaded');
      return;
    }

    mapboxgl.accessToken = this.mapboxToken;

    // Calculate map center from devices
    const center = this.calculateMapCenter();

    this.map = new mapboxgl.Map({
      container: 'map',
      style: 'mapbox://styles/mapbox/streets-v11',
      center: center,
      zoom: 2, // Start with a low zoom, will be adjusted by fitBounds
    });

    this.map.on('load', () => {
      this.addMarkersToMap();
    });
  }

  calculateMapCenter() {
    if (this.devices.length === 0) {
      return [-74.006, 40.7128]; // Default to NYC
    }

    const devicesWithLocation = this.devices.filter(d => d.location);
    if (devicesWithLocation.length === 0) {
      return [-74.006, 40.7128]; // Default to NYC
    }

    const totalLng = devicesWithLocation.reduce(
      (sum, d) => sum + d.location.longitude,
      0,
    );
    const totalLat = devicesWithLocation.reduce(
      (sum, d) => sum + d.location.latitude,
      0,
    );

    return [
      totalLng / devicesWithLocation.length,
      totalLat / devicesWithLocation.length,
    ];
  }

  addMarkersToMap() {
    // Check if map is loaded
    if (!this.map || !this.map.isStyleLoaded()) {
      console.warn(
        'BeeTrackDashboard: Map not ready, skipping marker addition',
      );
      return;
    }

    // Clear existing markers
    this.markers.forEach(marker => marker.remove());
    this.markers = [];

    // Create bounds to fit all markers
    const bounds = new mapboxgl.LngLatBounds();

    this.devices.forEach(device => {
      if (device.location) {
        const el = document.createElement('div');
        el.className = 'map-marker';
        el.style.width = '20px';
        el.style.height = '20px';
        el.style.borderRadius = '50%';

        // Color the selected device in blue, others based on active status
        if (this.selectedDevice && device.id === this.selectedDevice.id) {
          el.style.backgroundColor = '#244a92'; // Blue for selected device
        } else {
          el.style.backgroundColor = device.isActive ? '#4CAF50' : '#f44336';
        }

        el.style.border = '2px solid white';
        el.style.cursor = 'pointer';

        const coordinates = [
          device.location.longitude,
          device.location.latitude,
        ];

        const marker = new mapboxgl.Marker(el)
          .setLngLat(coordinates)
          .setPopup(
            new mapboxgl.Popup({ offset: 25 }).setHTML(`
                <h3>${device.deviceId}</h3>
                <p>Status: ${device.isActive ? 'Active' : 'Inactive'}</p>
                <p>Last seen: ${this.formatLastSeen(device.lastSeen)}</p>
                <button onclick="window.beeTrackDashboard.selectDevice('${device.id}')">View Data</button>
              `),
          )
          .addTo(this.map);

        marker.getElement().addEventListener('click', () => {
          this.selectDevice(device.id);
        });

        this.markers.push(marker);

        // Extend bounds to include this marker
        bounds.extend(coordinates);
      }
    });

    // Fit map to show all markers
    this.fitMapBounds(bounds);
  }

  fitMapBounds(bounds) {
    // Only fit bounds if we have valid bounds (at least one marker)
    if (!bounds.isEmpty()) {
      // Calculate the number of markers to determine appropriate padding
      const markerCount = this.markers.length;

      // Base padding that works well for most cases
      let padding = {
        top: 50,
        bottom: 50,
        left: 50,
        right: 50,
      };

      // If markers are very close together, use less padding and higher max zoom
      // If markers are far apart, use more padding
      let maxZoom = 15; // Default max zoom

      if (markerCount === 1) {
        // Single marker: zoom in closer
        maxZoom = 14;
        padding = { top: 100, bottom: 100, left: 100, right: 100 };
      } else if (markerCount <= 3) {
        // Few markers: allow closer zoom
        maxZoom = 13;
        padding = { top: 80, bottom: 80, left: 80, right: 80 };
      }

      this.map.fitBounds(bounds, {
        padding: padding,
        maxZoom: maxZoom,
      });
    } else {
      // No markers with location, fall back to default view
      this.map.setCenter([-74.006, 40.7128]);
      this.map.setZoom(10);
    }
  }

  setupEventListeners() {
    const deviceSelect = document.getElementById('deviceSelect');
    deviceSelect.addEventListener('change', e => {
      if (e.target.value && e.target.value !== this.selectedDevice?.id) {
        this.selectDevice(e.target.value);
      }
    });

    // Date range picker event listeners
    const startDateInput = document.getElementById('startDate');
    const endDateInput = document.getElementById('endDate');
    const updateRangeBtn = document.getElementById('updateRange');

    // Set default date range (last 7 days)
    const endDate = new Date();
    const startDate = new Date(endDate.getTime() - 7 * 24 * 60 * 60 * 1000);

    startDateInput.value = startDate.toISOString().split('T')[0];
    endDateInput.value = endDate.toISOString().split('T')[0];

    this.dateRange.startDate = startDate;
    this.dateRange.endDate = endDate;

    updateRangeBtn.addEventListener('click', async () => {
      const newStartDate = new Date(startDateInput.value);
      const newEndDate = new Date(endDateInput.value);

      if (newStartDate && newEndDate && newStartDate < newEndDate) {
        this.dateRange.startDate = newStartDate;
        this.dateRange.endDate = newEndDate;

        // Reload sensor data with new date range
        if (this.selectedDevice) {
          try {
            await this.loadDeviceSensorData(this.selectedDevice.id);
            this.updateCharts(); // Update charts with new data
          } catch (error) {
            console.error('Failed to update date range:', error);
            this.showError('Failed to update date range');
          }
        }
      } else {
        alert(
          'Please select a valid date range (start date must be before end date)',
        );
      }
    });
  }

  async selectDevice(deviceId) {
    try {
      this.selectedDevice = this.devices.find(d => d.id === deviceId);
      if (!this.selectedDevice) return;

      // Update device selector
      document.getElementById('deviceSelect').value = deviceId;

      // Update device info
      this.updateDeviceInfo();

      // Update map markers to show selected device in blue (only if map is ready)
      if (this.map && this.map.isStyleLoaded()) {
        this.addMarkersToMap();
      }

      // Load device statistics and sensor data
      await Promise.all([
        this.loadDeviceStatistics(deviceId),
        this.loadDeviceSensorData(deviceId),
      ]);

      // Update charts and stats
      this.updateCharts();
      this.updateStats();
    } catch (error) {
      console.error('Failed to select device:', error);
      this.showError('Failed to load device data');
    }
  }

  updateDeviceInfo() {
    const device = this.selectedDevice;
    if (!device) return;

    document.getElementById('selectedDeviceName').textContent = device.deviceId;
    document.getElementById('selectedDeviceStatus').textContent =
      `Status: ${device.isActive ? 'Active' : 'Inactive'}`;

    if (device.location) {
      document.getElementById('selectedDeviceLocation').textContent =
        `Location: ${device.location.latitude.toFixed(4)}, ${device.location.longitude.toFixed(4)}`;
    } else {
      document.getElementById('selectedDeviceLocation').textContent =
        'Location: Not available';
    }
  }

  async loadDeviceStatistics(deviceId) {
    try {
      const response = await httpx.get(
        `/api/v1/beeTrack/dashboard/devices/${deviceId}/statistics`,
      );
      this.deviceStats = response.data.data;
    } catch (error) {
      console.error('Failed to load device statistics:', error);
      throw error;
    }
  }

  async loadDeviceSensorData(deviceId) {
    try {
      const startDate = this.dateRange.startDate
        ? this.dateRange.startDate.toISOString()
        : null;
      const endDate = this.dateRange.endDate
        ? this.dateRange.endDate.toISOString()
        : null;

      const queryParams = new URLSearchParams();
      if (startDate) queryParams.append('startDate', startDate);
      if (endDate) queryParams.append('endDate', endDate);
      queryParams.append('aggregation', 'hour');
      queryParams.append('limit', '168');

      const response = await httpx.get(
        `/api/v1/beeTrack/dashboard/devices/${deviceId}/sensor-data?${queryParams.toString()}`,
      );
      this.sensorData = response.data.data;
    } catch (error) {
      console.error('Failed to load sensor data:', error);
      throw error;
    }
  }

  updateCharts() {
    if (!this.sensorData || !this.sensorData.sensorData) {
      return;
    }

    const data = this.sensorData.sensorData;
    const labels = data.map(d => this.formatChartDate(new Date(d.timestamp)));

    // Temperature Chart
    this.updateChart('temperatureChart', {
      labels,
      datasets: [
        {
          label: 'Temperature (°C)',
          data: data.map(d => d.avgTemperature),
          borderColor: '#ff6384',
          backgroundColor: 'rgba(255, 99, 132, 0.1)',
          tension: 0.1,
        },
      ],
    });

    // Humidity Chart
    this.updateChart('humidityChart', {
      labels,
      datasets: [
        {
          label: 'Humidity (%)',
          data: data.map(d => d.avgHumidity),
          borderColor: '#36a2eb',
          backgroundColor: 'rgba(54, 162, 235, 0.1)',
          tension: 0.1,
        },
      ],
    });

    // Weight Chart
    this.updateChart('weightChart', {
      labels,
      datasets: [
        {
          label: 'Weight (kg)',
          data: data.map(d => d.avgWeight),
          borderColor: '#ffcd56',
          backgroundColor: 'rgba(255, 205, 86, 0.1)',
          tension: 0.1,
        },
      ],
    });

    // Battery Chart with Signal Strength
    this.updateChart('batteryChart', {
      labels,
      datasets: [
        {
          label: 'Battery Level (%)',
          data: data.map(d => d.avgBatteryLevel),
          borderColor: '#4bc0c0',
          backgroundColor: 'rgba(75, 192, 192, 0.1)',
          tension: 0.1,
          yAxisID: 'y',
        },
        {
          label: 'Signal Strength (dBm)',
          data: data.map(d => d.avgSignalStrength),
          borderColor: '#ff9f40',
          backgroundColor: 'rgba(255, 159, 64, 0.1)',
          tension: 0.1,
          yAxisID: 'y1',
        },
      ],
    });
  }

  updateChart(chartId, data) {
    const canvas = document.getElementById(chartId);
    if (!canvas) return;

    // Destroy existing chart if it exists
    if (this.charts[chartId]) {
      this.charts[chartId].destroy();
    }

    // Special configuration for battery chart with dual Y-axes
    const isBatteryChart = chartId === 'batteryChart';

    const chartConfig = {
      type: 'line',
      data: data,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            display: true,
            title: {
              display: false,
            },
          },
          y: {
            display: true,
            title: {
              display: false,
            },
            position: 'left',
          },
          ...(isBatteryChart && {
            y1: {
              display: true,
              title: {
                display: false,
              },
              position: 'right',
              grid: {
                drawOnChartArea: false,
              },
            },
          }),
        },
        plugins: {
          legend: {
            display: true,
          },
        },
      },
    };

    // Create new chart
    this.charts[chartId] = new Chart(canvas, chartConfig);
  }

  updateStats() {
    const totalDevices = this.devices.length;
    const activeDevices = this.devices.filter(d => d.isActive).length;
    const inactiveDevices = totalDevices - activeDevices;

    // Update summary cards
    const totalDevicesEl = document.getElementById('totalDevices');
    const activeDevicesEl = document.getElementById('activeDevices');
    const inactiveDevicesEl = document.getElementById('inactiveDevices');
    const totalMeasurementsDisplayEl = document.getElementById(
      'totalMeasurementsDisplay',
    );

    if (totalDevicesEl) totalDevicesEl.textContent = totalDevices;
    if (activeDevicesEl) activeDevicesEl.textContent = activeDevices;
    if (inactiveDevicesEl) inactiveDevicesEl.textContent = inactiveDevices;

    if (this.deviceStats) {
      // Based on the API response structure: ['device', 'statistics', 'latestBatch', 'period', 'dateRange']
      const totalMeasurements =
        this.deviceStats.statistics?.totalMeasurements || 0;

      // Update total measurements display in chart controls
      if (totalMeasurementsDisplayEl) {
        totalMeasurementsDisplayEl.textContent = totalMeasurements;
      }
    }
  }

  formatChartDate(date) {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2); // Get last 2 digits of year
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${month}/${day}/${year} ${hours}:${minutes}`;
  }

  formatLastSeen(lastSeen) {
    if (!lastSeen) return 'Never';

    const now = new Date();
    const diff = now - new Date(lastSeen);
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days} days ago`;
    if (hours > 0) return `${hours} hours ago`;
    if (minutes > 0) return `${minutes} minutes ago`;
    return 'Just now';
  }

  showError(message) {
    // You can implement a proper error display mechanism here
    console.error(message);
    alert(message);
  }
}

// Initialize dashboard when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  window.beeTrackDashboard = new BeeTrackDashboard();
});
